import React from 'react';
import {
  FiBookOpen, FiCheckSquare, FiBarChart2, FiClipboard, FiBox, FiRadio, FiInbox,
  FiUsers, FiMail, FiMessageCircle, FiActivity, FiClock,
} from 'react-icons/fi';
import BrandIcon from './BrandIcon';
import IconCustomers from '../assets/Icon-Customers.svg';

// item 475 — the single source of truth for the TOOLS list, shared by the sidebar TOOLS group
// and the tools-modal (LayerPanel) header switcher, so you can hop between tools without exiting.
export interface ToolNavItem {
  key: string;
  icon: React.ReactElement;
  name: string;
  link: string;
  // item 1020 — extra route prefixes that count as "on this tool" for the active-chip
  // highlight (e.g. the item FORM /item/:id belongs to the Items tool at /items).
  match?: string[];
  // item 1022 (Chad picked "option 1") — the switcher strip chunks its chips into visual
  // groups with thin separators instead of one undifferentiated button row.
  group?: 'sales' | 'ops' | 'money';
  teamOk: boolean;
  // item 594 — visible to an Executive (ops/sales leader)? Defaults true; the FINANCIAL tools
  // (reports, accountant) set it false so an Executive gets no money surfaces.
  execOk?: boolean;
}

export const TOOLS: ToolNavItem[] = [
  // item 696 (Chad) — CRM moved back into the BASIX SIX sidebar group (removed from TOOLS/top bar to
  // avoid double-listing); it keeps its modal flow since /customers is a layer route.
  {
    key: 'sales-bible', icon: <FiBookOpen />, name: 'sales bible', link: '/sales-bible', group: 'sales', teamOk: true,
  },
  {
    key: 'department-tasks', icon: <FiCheckSquare />, name: 'department tasks', link: '/department-tasks', group: 'ops', teamOk: true,
  },
  // item 500 REVERTED (Chad, 2026-07-14) — the Item catalog is back in the TOOLS bar + tool modal.
  // item 1288 (Chad: "Team can see and add items, not archive" — Josh couldn't see Items at
  // all) — Team gets the catalog: view + add + inline edit. Archive stays locked server-side
  // to Admin/Accountant (item 1236) and its UI is isAdmin-gated (item 1286).
  {
    key: 'items', icon: <FiBox />, name: 'items', link: '/items', match: ['/item'], group: 'ops', teamOk: true,
  },
  // item 594 — reports + accountant are FINANCIAL: hidden from Executive (execOk: false).
  {
    key: 'reports', icon: <FiBarChart2 />, name: 'reports', link: '/reports', group: 'money', teamOk: false, execOk: false,
  },
  {
    key: 'accountant', icon: <FiClipboard />, name: 'accountant', link: '/accountant', group: 'money', teamOk: false, execOk: false,
  },
  // item 473 — the standalone Payroll command center is RETIRED; its flow lives on the
  // Accountant dashboard now (Approve extra pay + Payroll+).
  {
    // Team earns commissions too — they get their own book (Clients / My Commissions /
    // My Projects); the admin-only "All Commissions" tab stays gated inside the page.
    key: 'my-book', icon: <BrandIcon src={IconCustomers} color="currentColor" boxSize="22px" />, name: 'My Book', link: '/my-book', group: 'sales', teamOk: true,
  },
  // item 1194/1197 (My Pay+) — the timesheet + ic-invoices chips are RETIRED: hourly earners'
  // personal sheet + the IC upload live in My Book → My Pay+, and the admin review view is
  // My Book → All Timesheets (next to All Commissions). Both routes stay alive for deep links.
  // item 861 — live venue capacity for every deployed RNWY system (Ryan's status API);
  // not financial, so Team + Executive both get it.
  {
    key: 'rnwy-fleet', icon: <FiRadio />, name: 'RNWY fleet', link: '/rnwy-fleet', group: 'ops', teamOk: true,
  },
  // item 1337b (Chad: "will there be a my time section under tools?") — the work-order timer
  // home: running clock, unbooked pile, booked history (actual vs billed). Not financial.
  {
    key: 'my-time', icon: <FiClock />, name: 'my time', link: '/my-time', group: 'ops', teamOk: true,
  },
  // item 868a — the customer-portal pricing queue. Reviewing = PRICING, so it's a money
  // surface: admins/accountants only (no Team, no Executive).
  {
    key: 'customer-requests', icon: <FiInbox />, name: 'customer requests', link: '/customer-requests', group: 'sales', teamOk: false, execOk: false,
  },
  // item 500 — Appearance is a PERSONAL, per-device preference, not a company tool: it moved to
  // the user (avatar) menu. Its /appearance route still exists (opened from there).
];

// item 1022 (Chad) — the "bucket": secondary layer surfaces that don't earn a permanent chip
// (they have homes elsewhere — sidebar CRM, Accountant-page mailbox, chat bubble, KPI-strip live
// dashboard) but still need to be REACHABLE and HIGHLIGHTABLE from the switcher. They render as
// a single More menu at the end of the strip; when you're ON one, the bucket button itself
// becomes the lit chip carrying its name (supersedes most of item 1020's orphan injection).
export const SECONDARY_TOOLS: ToolNavItem[] = [
  {
    key: 'crm', icon: <FiUsers />, name: 'CRM', link: '/customers', match: ['/customer'], teamOk: true,
  },
  {
    key: 'chat', icon: <FiMessageCircle />, name: 'Chat', link: '/chat', teamOk: true,
  },
  {
    key: 'live-dashboard', icon: <FiActivity />, name: 'Live Dashboard', link: '/dashboard/live', teamOk: true,
  },
  // the accounting@ shared inbox — financial: admins/accountants only (no Team, no Executive)
  {
    key: 'mailbox', icon: <FiMail />, name: 'Mailbox', link: '/mailbox', teamOk: false, execOk: false,
  },
];

export const visibleSecondaryTools = (
  opts: { isTeam: boolean; isExecutive?: boolean },
): ToolNavItem[] => SECONDARY_TOOLS.filter((t) => (t.teamOk || !opts.isTeam)
  && (t.execOk !== false || !opts.isExecutive));

// Team users see only team-safe tools. Shared by the sidebar + the modal header so they
// never drift. (isHourly / isIndependentContractor stay in the signature for the callers,
// though item 1194 moved those earners' surfaces into My Book → My Pay+.)
export const visibleTools = (
  opts: {
    isTeam: boolean; isHourly: boolean; isExecutive?: boolean;
    isIndependentContractor?: boolean; // item 1079
    isAdmin?: boolean; // item 1080 — admins see ALL timesheets (no personal sheet)
  },
): ToolNavItem[] => TOOLS.filter((t) => (t.teamOk || !opts.isTeam)
  // item 594 — an Executive sees a tool unless it's explicitly execOk:false (financial)
  && (t.execOk !== false || !opts.isExecutive));
